import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@common/components/ui/form"
import { Input } from "@common/components/ui/input"
import { cn } from "@common/lib/utils"
import type { ComponentPropsWithoutRef } from "react"
import type { ControllerRenderProps, FieldValues, Path } from "react-hook-form"
import { useFormContext } from "react-hook-form"
import { FieldLabel } from "./label"

type BareProps<T extends FieldValues> = Omit<
  ComponentPropsWithoutRef<typeof Input>,
  "name" | "type" | "value" | "defaultValue" | "onChange" | "onBlur"
> & {
  name: Path<T>
  precision?: number
  optional?: boolean
}

export function BareDecimalField<T extends FieldValues>({
  name,
  optional,
  precision = 2,
  className,
  ...rest
}: BareProps<T>) {
  const { control } = useFormContext<T>()
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) =>
        renderDecimalInput<T>({
          field,
          invalid: !!fieldState.error,
          optional,
          precision,
          className,
          ...rest,
        })
      }
    />
  )
}

function renderDecimalInput<T extends FieldValues>({
  field,
  invalid,
  optional,
  precision = 2,
  className,
  ...rest
}: Omit<BareProps<T>, "name"> & {
  field: ControllerRenderProps<T, Path<T>>
  invalid: boolean
}) {
  const step = 10 ** -precision
  return (
    <Input
      type="number"
      step={step}
      className={cn(
        "text-right [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
        className,
      )}
      aria-required={!optional}
      aria-invalid={invalid ? true : undefined}
      {...rest}
      name={field.name}
      ref={field.ref}
      value={field.value ?? ""}
      onChange={(e) => field.onChange(e.target.value)}
      onBlur={(e) => {
        field.onBlur()
        const raw = e.target.value.trim()
        if (raw === "") {
          field.onChange("")
          return
        }
        const n = Number(raw)
        if (!Number.isFinite(n)) return
        field.onChange(n.toFixed(precision))
      }}
    />
  )
}

type Props<T extends FieldValues> = BareProps<T> & { label: string }

export function DecimalField<T extends FieldValues>({
  name,
  label,
  className,
  optional,
  precision,
  ...rest
}: Props<T>) {
  const { control } = useFormContext<T>()
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className={className}>
          <FieldLabel label={label} optional={optional} />
          <FormControl>
            {renderDecimalInput<T>({
              field,
              invalid: !!fieldState.error,
              optional,
              precision,
              ...rest,
            })}
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
